import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
    RegimeDependenceProfileReportRecord,
    RegimeModelVersionOverrides,
    RegimeProfileActiveModelVersionIds,
    TradingStrategy
} from '@/types/appRouter';
import { isTransient } from '@/api/isTransientError';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout/Toast';
import { Button, Callout, Card, EmptyState, FormSelect, StatusBadge } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { formatTimeHorizon } from '@/utils/timeHorizonGenerator';
import { selectDefaultProfileOutcomeSet, selectProfileHorizon } from './regimeProfileOutcomeSelection';
import { TRANSIENT_ERROR_RETRY_COUNT } from '@/config/constants';
import { useAppQuery } from '@/hooks/useAppQuery';
import { RegimeComparisonSection } from './RegimeComparisonSection';
import { RegimeInsufficientEvidenceChecklist } from './RegimeInsufficientEvidenceChecklist';
import { RegimeSliceTable } from './RegimeSliceTable';
import { RegimeSupportingDimensions } from './RegimeSupportingDimensions';
import { RegimeVerdictHeader } from './RegimeVerdictHeader';
import { RegimeVetoCandidateSection } from './RegimeVetoCandidateSection';
import {
    evaluateRegimeLineageStaleness,
    getPrimaryDimension,
    getRunPollIntervalMs,
    REGIME_DIMENSION_LABELS,
    selectMainLegReportSummary
} from './regimeProfilePresentation';

interface RegimeProfilePanelProps {
    experimentId: number;
    strategyId: number | null;
    /** Loaded strategy (for its current 1d regime model pins); null while loading or when unavailable. */
    strategy: TradingStrategy | null;
    executionStatus: string;
    durations: {
        duration: string;
        horizons: number[];
        metrics: Record<string, { optionAvgProfitPercent?: number | null; optionEvidence?: { completedCount: number } | null }>;
    }[];
    includeOptions: boolean;
    primaryExitCandidateId: number | null;
    initialDuration?: string;
    initialHorizonSeconds?: number | null;
}

/**
 * Design rule 6 — a report whose resolved model versions no longer match what a run would resolve today
 * is a wrong number, not a stale one. Each scope is judged against the strategy's current 1d pin when
 * one exists (pins take resolution precedence), otherwise against the current active model version from
 * `listForStrategy.activeModelVersionIds`; a scope with neither is undecidable and never banners.
 * `currentStrategyPins` is `undefined` while the strategy has not loaded, `null` when it carries no pins;
 * `activeModelVersionIds` is `undefined` while the list has not loaded.
 */
export function StaleLineageBanner({
    record,
    currentStrategyPins,
    activeModelVersionIds
}: {
    record: RegimeDependenceProfileReportRecord;
    currentStrategyPins: RegimeModelVersionOverrides | null | undefined;
    activeModelVersionIds: RegimeProfileActiveModelVersionIds | undefined;
}) {
    const staleness = evaluateRegimeLineageStaleness({
        activeModelVersionIds,
        currentStrategyPins,
        resolvedModelVersionIds: record.report.lineage.resolvedModelVersionIds
    });

    if (staleness.status !== 'stale') return null;

    return (
        <Callout title="Regenerate — profiled under a superseded model" variant="warning">
            <div className="space-y-1">
                {staleness.mismatches.map((mismatch) => (
                    <div key={mismatch.scope}>
                        {mismatch.scope} scope: profiled under version {mismatch.resolvedVersionId};{' '}
                        {mismatch.basis === 'pin'
                            ? `the strategy now pins version ${mismatch.currentVersionId}.`
                            : `the active model is now version ${mismatch.currentVersionId}.`}
                    </div>
                ))}
                <div>
                    Two profiles of the same strategy can legitimately disagree when they resolved different classifiers — re-run the
                    profile before acting on this report.
                </div>
            </div>
        </Callout>
    );
}

export function RegimeProfilePanel({
    experimentId,
    strategyId,
    strategy,
    executionStatus,
    durations,
    includeOptions,
    primaryExitCandidateId,
    initialDuration,
    initialHorizonSeconds = null
}: RegimeProfilePanelProps) {
    const [durationChoice, setDurationChoice] = useState<string | null>(null);
    const [outcomeChoice, setOutcomeChoice] = useState<string | null>(null);
    const [horizonChoice, setHorizonChoice] = useState<number | null>(null);
    const selectedDuration =
        durationChoice ??
        (durations.some((item) => item.duration === initialDuration)
            ? (initialDuration ?? '')
            : durations.length === 1
              ? durations[0].duration
              : '');
    const durationResult = durations.find((item) => item.duration === selectedDuration);
    const outcomeSetsQuery = useAppQuery(
        {
            ...trpc.experiment.listReadyRealizedOutcomeSets.queryOptions({
                experimentId,
                ...(selectedDuration ? { duration: selectedDuration } : {})
            }),
            enabled: strategyId !== null && selectedDuration !== ''
        },
        { fallbackErrorMessage: 'Failed to load realized outcomes for regime profiling' }
    );
    const compatibleOutcomeSets = (outcomeSetsQuery.data?.outcomeSets ?? []).filter(
        (set) => set.strategyId === strategyId && set.duration === selectedDuration
    );
    const defaultOutcomeSet = selectDefaultProfileOutcomeSet(compatibleOutcomeSets, strategyId, selectedDuration, primaryExitCandidateId);
    const selectedOutcome =
        outcomeChoice ?? (defaultOutcomeSet ? `realized:${defaultOutcomeSet.id}` : includeOptions ? 'option' : 'underlying');
    const realizedOutcomeSet = compatibleOutcomeSets.find((set) => selectedOutcome === `realized:${set.id}`) ?? null;
    const isRealized = selectedOutcome.startsWith('realized:');
    const availableHorizons = (durationResult?.horizons ?? []).filter(
        (horizon) =>
            selectedOutcome !== 'option' ||
            (durationResult?.metrics[String(horizon)]?.optionEvidence?.completedCount ?? 0) > 0 ||
            durationResult?.metrics[String(horizon)]?.optionAvgProfitPercent != null
    );
    const selectedHorizon = selectProfileHorizon(availableHorizons, horizonChoice ?? initialHorizonSeconds);
    const [activeJobId, setActiveJobId] = useState<number | null>(null);
    const previousRunStatusRef = useRef<string | null>(null);

    // Server-filtered to this experiment's whole verdict history: client-side filtering of the
    // strategy-wide page would report "never profiled" once newer runs push this experiment's reports
    // past the page limit.
    const listQuery = useAppQuery(
        {
            ...trpc.regimeProfile.listForStrategy.queryOptions({ experimentId, strategyId: strategyId ?? 0, duration: selectedDuration }),
            enabled: strategyId !== null && selectedDuration !== ''
        },
        { fallbackErrorMessage: 'Failed to load regime profile history' }
    );

    const mainLegSelection = useMemo(
        () =>
            selectMainLegReportSummary(listQuery.data?.reports ?? [], experimentId, {
                outcomeMode: isRealized ? 'realizedStrategyExit' : 'fixedHorizon',
                outcomeBasis: isRealized ? null : selectedOutcome,
                horizonSeconds: selectedHorizon,
                realizedOutcomeSetId: realizedOutcomeSet?.id ?? null
            }),
        [experimentId, listQuery.data?.reports, isRealized, selectedOutcome, selectedHorizon, realizedOutcomeSet?.id]
    );
    const latestSummary = mainLegSelection.summary;

    const reportQuery = useAppQuery(
        {
            ...trpc.regimeProfile.getById.queryOptions({ reportId: latestSummary?.id ?? 0 }),
            enabled: latestSummary !== null
        },
        { fallbackErrorMessage: 'Failed to load the regime profile report' }
    );

    const runMutation = useMutation(
        trpc.regimeProfile.run.mutationOptions({
            onError: (error) => showToast(error.message, 'error'),
            onSuccess: (result) => {
                if (result.status === 'conflict') {
                    showToast(`${result.message} Showing the run already in progress.`, 'warning');
                    previousRunStatusRef.current = null;
                    setActiveJobId(result.activeJob.id);
                    return;
                }

                showToast(
                    result.status === 'joined' ? 'Joined the profile run already in progress.' : 'Regime profile run started.',
                    'info'
                );
                previousRunStatusRef.current = null;
                setActiveJobId(result.jobId);
            }
        })
    );

    const runStatusQuery = useQuery({
        ...trpc.regimeProfile.getRun.queryOptions({ jobId: activeJobId ?? 0 }),
        enabled: activeJobId !== null,
        refetchInterval: (query) =>
            getRunPollIntervalMs({ isError: query.state.status === 'error', jobStatus: query.state.data?.job.status }),
        // React Query pauses interval refetches for unfocused documents by default, which froze the
        // "Profile running" badge on a completed job during the first production run (verified: the
        // job finished server-side in 10s while zero getRun polls fired from an unfocused tab). The
        // poll is bounded — it only runs while a job is active — so polling in the background is cheap
        // and keeps the badge truthful when the user switches tabs mid-run.
        refetchIntervalInBackground: true,
        retry: (failureCount, error) => isTransient(error) && failureCount < TRANSIENT_ERROR_RETRY_COUNT
    });

    // A poll that lands in error state (after transient retries) must not wedge the panel: without this,
    // a failed first poll shows "Profile pending" forever with the Run button disabled, and an error
    // after success would keep re-polling a dead job.
    useEffect(() => {
        if (!runStatusQuery.isError || activeJobId === null) return;

        showToast(runStatusQuery.error instanceof Error ? runStatusQuery.error.message : 'Failed to poll the regime profile run.', 'error');
        previousRunStatusRef.current = null;
        setActiveJobId(null);
    }, [activeJobId, runStatusQuery.error, runStatusQuery.isError]);

    const { refetch: refetchList } = listQuery;
    const { refetch: refetchReport } = reportQuery;

    useEffect(() => {
        const job = runStatusQuery.data?.job;

        if (!job || activeJobId === null) return;

        const previousStatus = previousRunStatusRef.current;
        previousRunStatusRef.current = job.status;

        if (previousStatus === job.status) return;

        if (job.status === 'completed') {
            showToast('Regime profile completed.', 'success');
            setActiveJobId(null);
            void refetchList();
            void refetchReport();
        } else if (job.status === 'failed') {
            showToast(job.errorMessage ?? 'Regime profile run failed.', 'error');
            setActiveJobId(null);
        }
    }, [activeJobId, refetchList, refetchReport, runStatusQuery.data?.job]);

    const activeRunStatus = activeJobId !== null ? (runStatusQuery.data?.job.status ?? 'pending') : null;
    const canRun =
        strategyId !== null &&
        executionStatus === 'completed' &&
        activeJobId === null &&
        !runMutation.isPending &&
        selectedDuration !== '' &&
        outcomeSetsQuery.isSuccess &&
        (isRealized ? realizedOutcomeSet !== null : selectedHorizon !== null);

    const startRun = () => {
        if (!canRun || strategyId === null) return;

        if (isRealized && realizedOutcomeSet !== null) {
            runMutation.mutate({
                experimentId,
                strategyId,
                duration: selectedDuration,
                outcomeMode: 'realizedStrategyExit',
                realizedOutcomeSetId: realizedOutcomeSet.id,
                expectedExitLineage: {
                    tradeLogicV2ExitVersionId: realizedOutcomeSet.tradeLogicV2ExitVersionId,
                    exitFingerprint: realizedOutcomeSet.exitFingerprint
                }
            });
        } else if (selectedHorizon !== null && (selectedOutcome === 'option' || selectedOutcome === 'underlying')) {
            runMutation.mutate({
                experimentId,
                strategyId,
                duration: selectedDuration,
                outcomeMode: 'fixedHorizon',
                outcomeBasis: selectedOutcome,
                horizonSeconds: selectedHorizon
            });
        }
    };

    const runControls = (
        <div className="flex flex-wrap items-center gap-2">
            <p className="w-full text-xs text-zinc-400">
                Uses daily regime context for stock, index, and mixed experiments, including option outcomes.
            </p>
            {durations.length > 1 && (
                <FormSelect
                    noMargin
                    aria-label="Profile duration"
                    options={[
                        { label: 'Choose duration', value: '' },
                        ...durations.map((duration) => ({ label: duration.duration, value: duration.duration }))
                    ]}
                    value={selectedDuration}
                    onChange={(event) => {
                        setDurationChoice(event.target.value);
                        setOutcomeChoice(null);
                        setHorizonChoice(null);
                    }}
                />
            )}
            <FormSelect
                noMargin
                aria-label="Profile outcomes"
                value={selectedOutcome}
                options={[
                    ...compatibleOutcomeSets.map((set) => ({
                        label: `Realized exit #${set.exitCandidateId ?? 'custom'} · set #${set.id}`,
                        value: `realized:${set.id}`
                    })),
                    { label: 'Fixed horizon · underlying', value: 'underlying' },
                    ...(includeOptions ? [{ label: 'Fixed horizon · option', value: 'option' }] : [])
                ]}
                onChange={(event) => {
                    setOutcomeChoice(event.target.value);
                    setHorizonChoice(null);
                }}
            />
            {!isRealized && (
                <FormSelect
                    noMargin
                    aria-label="Profile horizon"
                    value={selectedHorizon === null ? '' : String(selectedHorizon)}
                    options={[
                        { label: 'Choose horizon', value: '' },
                        ...availableHorizons.map((horizon) => ({ label: formatTimeHorizon(horizon), value: String(horizon) }))
                    ]}
                    onChange={(event) => {
                        setOutcomeChoice(selectedOutcome);
                        setHorizonChoice(event.target.value ? Number(event.target.value) : null);
                    }}
                />
            )}
            {outcomeSetsQuery.isError && (
                <>
                    <span className="text-xs text-amber-300">Outcome selection could not load.</span>
                    <Button size="sm" variant="secondary" onClick={() => void outcomeSetsQuery.refetch()}>
                        Retry outcomes
                    </Button>
                </>
            )}
            {outcomeSetsQuery.isSuccess && compatibleOutcomeSets.length > 1 && outcomeChoice === null && defaultOutcomeSet === null && (
                <span className="text-xs text-zinc-400">
                    Several realized outcome sets exist. Select the intended exit, or use the displayed fixed-horizon basis.
                </span>
            )}
            {activeRunStatus !== null && (
                <StatusBadge pulse={activeRunStatus === 'pending' || activeRunStatus === 'running'} status={activeRunStatus}>
                    Profile {activeRunStatus}
                </StatusBadge>
            )}
            <Button disabled={!canRun} size="sm" variant="secondary" onClick={startRun}>
                {latestSummary !== null ? 'Re-run profile' : 'Run profile'}
            </Button>
        </div>
    );

    if (strategyId === null) {
        return (
            <Card className="bg-zinc-900">
                <p className="text-sm text-zinc-400">Regime profiling requires this experiment to belong to a strategy.</p>
            </Card>
        );
    }

    if (listQuery.isLoading || (latestSummary !== null && reportQuery.isLoading)) {
        return (
            <Card className="bg-zinc-900">
                <DataLoadingSpinner text="Loading regime profile..." />
            </Card>
        );
    }

    const reportResult = latestSummary !== null ? reportQuery.data : undefined;

    if (reportResult?.status === 'unreadable') {
        return (
            <div className="space-y-4">
                <Callout title="Stored report cannot be read by this build" variant="warning">
                    A regime profile report exists for this experiment, but this version of the app cannot decode it ({reportResult.code}
                    ): {reportResult.message}. Re-run the profile to produce a readable report.
                </Callout>
                <Card className="bg-zinc-900">{runControls}</Card>
            </div>
        );
    }

    const record =
        reportResult?.status === 'ok' && reportResult.report.report.lineage.duration === selectedDuration ? reportResult.report : null;

    if (record === null || record === undefined) {
        return (
            <Card className="bg-zinc-900">
                <EmptyState
                    action={runControls}
                    description={
                        executionStatus === 'completed'
                            ? 'Check whether results depend on market regimes. Run this early or after other optimization when it can help choose a useful variant.'
                            : 'The profile becomes available once the experiment completes: it slices realized training-window trades, so there is nothing to profile yet.'
                    }
                    title="No regime profile yet"
                />
            </Card>
        );
    }

    const primaryLabel = REGIME_DIMENSION_LABELS[getPrimaryDimension(record.report).key];
    const displayedModeLabel = record.outcomeMode === 'fixedHorizon' ? 'fixed horizon' : 'realized strategy exit';

    // Design rule 6, applied to the action path too: the veto card's "Create gated variant" must not
    // act on a report profiled under a superseded model. Same inputs as StaleLineageBanner.
    const lineageStaleness = evaluateRegimeLineageStaleness({
        activeModelVersionIds: listQuery.data?.activeModelVersionIds,
        currentStrategyPins: strategy === null ? undefined : (strategy.regimeModelVersionOverrides ?? null),
        resolvedModelVersionIds: record.report.lineage.resolvedModelVersionIds
    });

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <h2 className="text-base font-semibold text-white">Regime dependence profile</h2>
                    <p className="mt-0.5 text-xs text-zinc-500">
                        Showing duration {record.report.lineage.duration} · {displayedModeLabel}
                    </p>
                </div>
                {runControls}
            </div>

            <StaleLineageBanner
                activeModelVersionIds={listQuery.data?.activeModelVersionIds}
                currentStrategyPins={strategy === null ? undefined : (strategy.regimeModelVersionOverrides ?? null)}
                record={record}
            />

            <RegimeVerdictHeader record={record} />

            {record.report.warnings.length > 0 && (
                <div className="rounded border border-amber-900/60 bg-amber-950/20 px-3 py-2 text-xs text-amber-300/90">
                    {record.report.warnings.map((warning) => (
                        <div key={warning.code}>
                            <span className="font-medium">
                                <span aria-hidden>⚠︎ </span>
                                {warning.code}
                            </span>
                            : {warning.detail}
                        </div>
                    ))}
                </div>
            )}

            {record.report.verdict.verdict === 'insufficient_evidence' && <RegimeInsufficientEvidenceChecklist report={record.report} />}

            <Card className="bg-zinc-900 border border-zinc-800">
                <h3 className="mb-3 text-sm font-medium text-zinc-100">{primaryLabel}</h3>
                <RegimeSliceTable
                    checkUnclassifiedShare
                    showRetention
                    dimension={getPrimaryDimension(record.report)}
                    thresholds={record.report.thresholds}
                />
            </Card>

            <RegimeVetoCandidateSection lineageStale={lineageStaleness.status === 'stale'} record={record} strategy={strategy} />

            <RegimeSupportingDimensions report={record.report} />

            {record.outcomeMode === 'fixedHorizon' && (
                <RegimeComparisonSection experimentId={experimentId} mainLegRecord={record} summaries={listQuery.data?.reports ?? []} />
            )}
        </div>
    );
}
